"use client";

import { BookSearch, Loader2, Search } from "lucide-react";
import { useParams } from "next/navigation";
import type { FormEvent } from "react";
import {
  BookResultCard,
  BookResultCardSkeleton,
  type BookResultData,
} from "@/components/book-result-card";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCount, t } from "@/lib/i18n";

interface BookSearchFormProps {
  query: string;
  onQueryChange: (query: string) => void;
  onSubmit: (e: FormEvent) => void;
  loading: boolean;
  results: BookResultData[];
  totalResults: number;
  onSelectBook: (book: BookResultData) => void;
  error: string | null;
}

export function BookSearchForm({
  query,
  onQueryChange,
  onSubmit,
  loading,
  results,
  totalResults,
  onSelectBook,
  error,
}: BookSearchFormProps) {
  const s = useToolStrings();
  const params = useParams<{ locale?: string }>();
  const locale = params?.locale ?? "en";
  return (
    <div className="space-y-4">
      <form className="space-y-2" onSubmit={onSubmit}>
        <Label htmlFor="book-search">{s.bookSearchLabel}</Label>
        <div className="flex gap-2">
          <Input
            className="flex-1"
            id="book-search"
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="e.g. To Kill a Mockingbird, Harper Lee..."
            type="search"
            value={query}
          />
          <Button disabled={loading} type="submit">
            {loading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Search className="size-4" />
            )}
            {s.search}
          </Button>
        </div>
        <p className="text-muted-foreground text-xs">{s.bookSearchSource}</p>
      </form>

      {/* Loading Skeletons */}
      {loading && (
        <div className="space-y-3">
          {["skeleton-1", "skeleton-2", "skeleton-3"].map((id) => (
            <BookResultCardSkeleton key={id} />
          ))}
        </div>
      )}

      {/* Book Results */}
      {!loading && results.length > 0 && (
        <div className="space-y-3">
          {totalResults > 0 && (
            <p className="text-muted-foreground text-sm">
              {t(s.showingResults, {
                shown: results.length,
                total: formatCount(locale, totalResults),
              })}
            </p>
          )}
          <div className="max-h-[450px] space-y-3 overflow-y-auto pe-1">
            {results.map((book) => (
              <BookResultCard
                book={book}
                key={book.key}
                onSelect={onSelectBook}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!(loading || error) && results.length === 0 && query && (
        <div className="py-8 text-center text-muted-foreground">
          <BookSearch className="mx-auto mb-3 size-12 opacity-40" />
          <p>{s.bookSearchEmpty}</p>
        </div>
      )}
    </div>
  );
}
